/**
 * Pure completion logic — no DOM, so tests can import it directly (like
 * core/normalize.ts). Everything geometric lives in overlay.ts instead.
 */

export const MIN_TOKEN_LENGTH = 2;

/**
 * The trailing alphabetic run the visitor is typing, or null when there is
 * nothing to complete: caret not at the logical end of the value (completing
 * mid-string would fight the caret), token shorter than MIN_TOKEN_LENGTH, or
 * the value ends in something other than letters (digits and apostrophes end
 * a token — the wordlist is plain lowercase a–z).
 */
export function currentToken(
  value: string,
  selStart: number | null,
  selEnd: number | null,
): string | null {
  if (selStart === null || selEnd === null) return null;
  if (selStart !== value.length || selEnd !== value.length) return null;
  const match = /[a-zA-Z]+$/.exec(value);
  if (!match || match[0].length < MIN_TOKEN_LENGTH) return null;
  return match[0];
}

/**
 * The remainder that completes `token` to the first (= highest-frequency,
 * the list is frequency-descending) word that starts with it — strictly
 * longer, so a fully-typed word never ghosts itself. `maxLength > 0` is the
 * input's maxlength; a completion that would overflow it is skipped rather
 * than truncated to a non-word.
 */
export function findCompletion(
  words: readonly string[],
  token: string,
  valueLength: number,
  maxLength: number,
): string | null {
  const needle = token.toLowerCase();
  for (const word of words) {
    if (word.length <= needle.length || !word.startsWith(needle)) continue;
    const remainder = word.slice(needle.length);
    if (maxLength > 0 && valueLength + remainder.length > maxLength) continue;
    return remainder;
  }
  return null;
}

/**
 * The standalone-input Enter target: `{page}?query=…`. `query` is the param
 * Webflow's native Search element submits and initGlobalSearch() reads, so a
 * plain search page picks the term up with no extra wiring. `page` may be
 * site-relative ("/search") or absolute (another domain's search page).
 */
export function searchPageUrl(page: string, query: string, base: string): string {
  const url = new URL(page, base);
  url.searchParams.set("query", query);
  return url.toString();
}
